import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ALL_ROLES, PERMISSIONS, type Role } from '@lims/permissions';
import { ALL_LOCALES, type Locale } from '@lims/shared-types';
import { createUserSchema } from '@lims/shared-validation';
import { ApiError } from '../../api/client.js';
import { usersApi, type UserDetail } from '../../api/endpoints.js';
import { useAuth } from '../../app/auth-context.js';
import {
  ActionBar,
  ActionButton,
  Alert,
  ConfirmDialog,
  Dialog,
  ErrorState,
  Form,
  FormSection,
  hasNewPasswordProblem,
  LoadingState,
  NewPasswordFields,
  PageHeader,
  Panel,
  ReadOnlyField,
  SelectField,
  StatusBadge,
  TextAreaField,
  TextField,
  useToast,
  SaveErrorNotice,
} from '../../components/ui/index.js';
import { formatDateTime } from '../../components/format.js';
import { currentLocale } from '../../i18n/index.js';
import { PermissionEditor } from './PermissionEditor.js';

/**
 * Create or edit a user.
 *
 * CREATION IS COMPLETE IN ONE STEP (D179). The manager sets the first password
 * here and the account signs in the moment it is saved. Nothing is emailed:
 * the invitation link this page used to promise depended on the identity
 * provider's redirect settings, and a broken link left new colleagues unable
 * to start. The person can change the password from their own profile.
 *
 * A MANAGER CAN SET A NEW PASSWORD (D180) for anyone else in the laboratory,
 * without the old one. It needs `users.update` AND `permissions.manage` — the
 * page offers it only then, and the server checks both regardless. Every
 * session that person holds ends. The page never offers it for the manager's
 * own account, which is changed from the profile with the current password.
 *
 * There is no delete control, and no endpoint behind one. Deactivation is the
 * only way to remove someone's access, so their name stays on the work they
 * did (requirements p.20).
 */
export function UserFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === undefined || id === 'new';
  return isNew ? <CreateUserPage /> : <UserDetailPage id={id} />;
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

function useRoleOptions() {
  const { t } = useTranslation();
  return ALL_ROLES.map((value) => ({ value, label: t(`roles.${value}`) }));
}

function useLanguageOptions() {
  const { t } = useTranslation();
  return ALL_LOCALES.map((value) => ({
    value,
    label: value === 'ar' ? t('common.arabic') : t('common.english'),
  }));
}

/** Field-level messages from the SHARED schema, translated by issue kind. */
function fieldErrors(
  values: Record<string, unknown>,
  t: (key: string) => string,
): Partial<Record<'displayName' | 'email' | 'role', string>> {
  const result = createUserSchema
    .pick({ displayName: true, email: true, role: true })
    .safeParse(values);
  if (result.success) return {};
  const errors: Partial<Record<'displayName' | 'email' | 'role', string>> = {};
  for (const issue of result.error.issues) {
    const field = issue.path[0] as 'displayName' | 'email' | 'role';
    if (errors[field]) continue;
    const empty = values[field] === '' || values[field] === null;
    errors[field] = empty
      ? t('validation.required')
      : issue.code === 'too_small'
        ? t('validation.tooShort')
        : t('validation.invalidFormat');
  }
  return errors;
}

// ---------------------------------------------------------------------------
// Create
// ---------------------------------------------------------------------------

function CreateUserPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const roleOptions = useRoleOptions();
  const languageOptions = useLanguageOptions();

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role | null>(null);
  // Null: the laboratory's own default language, applied by the server.
  const [preferredLocale, setPreferredLocale] = useState<Locale | null>(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const create = useMutation({
    mutationFn: () =>
      usersApi.create({
        email: email.trim(),
        displayName: displayName.trim(),
        role: role as Role,
        password,
        ...(preferredLocale ? { preferredLanguage: preferredLocale } : {}),
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      show({ tone: 'success', message: t('users.created', { name: created.displayName }) });
      navigate(`/users/${created.id}`, { replace: true });
    },
  });

  const errors = hasSubmitted ? fieldErrors({ displayName, email, role }, t) : {};
  const emailInUse =
    create.error instanceof ApiError && create.error.code === 'DUPLICATE_RECORD';
  const emailError = errors.email ?? (emailInUse ? t('users.emailInUse') : undefined);
  const passwordInvalid = hasNewPasswordProblem(password, confirmation);
  const errorCount =
    Object.keys(errors).length + (passwordInvalid ? 1 : 0);

  const submit = () => {
    setHasSubmitted(true);
    const invalid =
      Object.keys(fieldErrors({ displayName, email, role }, t)).length > 0 || passwordInvalid;
    if (invalid) return;
    create.mutate();
  };

  return (
    <>
      <PageHeader
        title={t('users.createTitle')}
        breadcrumbs={[
          { label: t('nav.users'), to: '/users' },
          { label: t('users.createTitle') },
        ]}
      />

      <div className="max-w-4xl">
        <Form
          onSubmit={submit}
          isSubmitting={create.isPending}
          hasSubmitted={hasSubmitted}
          errorCount={errorCount}
          actions={
            <ActionBar>
              <ActionButton priority="tertiary" onPress={() => navigate('/users')}>
                {t('common.cancel')}
              </ActionButton>
              <ActionButton priority="primary" type="submit" isLoading={create.isPending}>
                {t('users.createSubmit')}
              </ActionButton>
            </ActionBar>
          }
        >
          <FormSection title={t('users.accountSection')} description={t('users.accountSectionHint')}>
            <TextField
              label={t('users.name')}
              value={displayName}
              onChange={setDisplayName}
              autoComplete="off"
              isRequired
              {...(errors.displayName ? { error: errors.displayName } : {})}
            />
            <TextField
              type="email"
              label={t('users.email')}
              value={email}
              onChange={setEmail}
              // Not the manager's own address: stop the browser offering it.
              autoComplete="off"
              // An address is left-to-right text on an Arabic page too.
              textDirection="ltr"
              isRequired
              {...(emailError ? { error: emailError } : {})}
            />
          </FormSection>

          <FormSection title={t('users.roleSection')} description={t('users.roleSectionHint')}>
            <SelectField
              label={t('users.role')}
              placeholder={t('users.chooseRole')}
              value={role}
              onChange={(next) => setRole(next as Role | null)}
              options={roleOptions}
              isRequired
              {...(errors.role ? { error: errors.role } : {})}
            />
            <SelectField
              label={t('users.language')}
              placeholder={t('users.languageDefault')}
              description={t('users.languageDefaultHint')}
              value={preferredLocale}
              onChange={(next) => setPreferredLocale(next as Locale | null)}
              options={languageOptions}
            />
          </FormSection>

          <FormSection title={t('users.signInSection')} columns={1}>
            <Alert tone="info" title={t('users.directCreationNotice')} />
            <div className="max-w-xl">
              <NewPasswordFields
                password={password}
                confirmation={confirmation}
                onPasswordChange={setPassword}
                onConfirmationChange={setConfirmation}
                showProblems={hasSubmitted}
                passwordLabel={t('users.password')}
                confirmationLabel={t('users.confirmPassword')}
              />
            </div>
          </FormSection>

          {create.error && !emailInUse ? <SaveErrorNotice error={create.error} fieldLabels={{ displayName: t('users.name'), email: t('users.email'), role: t('users.role') }} /> : null}
        </Form>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Detail and edit
// ---------------------------------------------------------------------------

function UserDetailPage({ id }: { readonly id: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser, can } = useAuth();

  const existing = useQuery({
    queryKey: ['user', id],
    queryFn: () => usersApi.get(id),
  });

  if (existing.isLoading) return <LoadingState label={t('common.loading')} />;
  if (existing.error || !existing.data) {
    return (
      <ErrorState
        error={existing.error}
        onRetry={() => void existing.refetch()}
      />
    );
  }

  const record = existing.data;
  const isSelf = currentUser?.id === record.id;

  return (
    <>
      <PageHeader
        title={record.displayName}
        subtitle={record.email}
        breadcrumbs={[
          { label: t('nav.users'), to: '/users' },
          { label: record.displayName },
        ]}
        actions={
          <ActionButton priority="tertiary" onPress={() => navigate('/users')}>
            {t('common.back')}
          </ActionButton>
        }
      />

      <div className="grid min-w-0 gap-gutter xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
        <div className="flex min-w-0 flex-col gap-gutter">
          <AccountDetails record={record} isSelf={isSelf} canUpdate={can(PERMISSIONS.USERS_UPDATE)} />
          <AccountAccess
            record={record}
            isSelf={isSelf}
            canUpdate={can(PERMISSIONS.USERS_UPDATE)}
            canSetPassword={
              can(PERMISSIONS.USERS_UPDATE) && can(PERMISSIONS.PERMISSIONS_MANAGE)
            }
          />
        </div>

        <Panel title={t('permissions.title')} description={t('permissions.subtitle')}>
          <PermissionEditor
            user={record}
            canEdit={can(PERMISSIONS.PERMISSIONS_MANAGE)}
            onSaved={async () => {
              await queryClient.invalidateQueries({ queryKey: ['user', id] });
            }}
          />
        </Panel>
      </div>
    </>
  );
}

function AccountDetails({
  record,
  isSelf,
  canUpdate,
}: {
  readonly record: UserDetail;
  readonly isSelf: boolean;
  readonly canUpdate: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const roleOptions = useRoleOptions();
  const languageOptions = useLanguageOptions();

  const [displayName, setDisplayName] = useState(record.displayName);
  const [email, setEmail] = useState(record.email);
  const [role, setRole] = useState<Role>(record.role);
  const [preferredLocale, setPreferredLocale] = useState<Locale>(
    record.preferredLocale as Locale,
  );
  const [reason, setReason] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    setDisplayName(record.displayName);
    setEmail(record.email);
    setRole(record.role);
    setPreferredLocale(record.preferredLocale as Locale);
  }, [record]);

  const update = useMutation({
    mutationFn: () =>
      usersApi.update(record.id, {
        email: email.trim(),
        displayName: displayName.trim(),
        // A user cannot change their own role; the server refuses it either way.
        ...(isSelf ? {} : { role }),
        preferredLanguage: preferredLocale,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['user', record.id] });
      setReason('');
      setHasSubmitted(false);
      show({ tone: 'success', message: t('users.saved') });
    },
  });

  const isReadOnly = !canUpdate;
  const errors = hasSubmitted ? fieldErrors({ displayName, email, role }, t) : {};
  const emailInUse =
    update.error instanceof ApiError && update.error.code === 'DUPLICATE_RECORD';
  const emailError = errors.email ?? (emailInUse ? t('users.emailInUse') : undefined);
  const isDirty =
    displayName !== record.displayName ||
    email !== record.email ||
    role !== record.role ||
    preferredLocale !== record.preferredLocale;

  return (
    <Panel>
      <Form
        onSubmit={() => {
          setHasSubmitted(true);
          if (Object.keys(fieldErrors({ displayName, email, role }, t)).length > 0) return;
          update.mutate();
        }}
        isSubmitting={update.isPending}
        hasSubmitted={hasSubmitted}
        errorCount={Object.keys(errors).length}
        actions={
          isReadOnly ? null : (
            <ActionBar>
              <ActionButton
                priority="primary"
                type="submit"
                isLoading={update.isPending}
                isDisabled={!isDirty}
              >
                {t('common.save')}
              </ActionButton>
            </ActionBar>
          )
        }
      >
        <FormSection title={t('users.detailsSection')} columns={1}>
          <TextField
            label={t('users.name')}
            value={displayName}
            onChange={setDisplayName}
            isRequired
            isReadOnly={isReadOnly}
            {...(errors.displayName ? { error: errors.displayName } : {})}
          />
          <TextField
            type="email"
            label={t('users.email')}
            value={email}
            onChange={setEmail}
            autoComplete="off"
            textDirection="ltr"
            isRequired
            isReadOnly={isReadOnly}
            {...(emailError ? { error: emailError } : {})}
          />
          <SelectField
            label={t('users.role')}
            value={role}
            onChange={(next) => {
              if (next) setRole(next as Role);
            }}
            options={roleOptions}
            isDisabled={isReadOnly || isSelf}
            {...(isSelf ? { description: t('users.cannotEditSelfRole') } : {})}
          />
          <SelectField
            label={t('users.language')}
            value={preferredLocale}
            onChange={(next) => {
              if (next) setPreferredLocale(next as Locale);
            }}
            options={languageOptions}
            isDisabled={isReadOnly}
          />
          {!isReadOnly ? (
            <TextAreaField
              label={t('common.reasonOptional')}
              value={reason}
              onChange={setReason}
              rows={2}
            />
          ) : null}
        </FormSection>

        {update.error && !emailInUse ? <SaveErrorNotice error={update.error} fieldLabels={{ displayName: t('users.name'), email: t('users.email'), role: t('users.role') }} /> : null}
      </Form>
    </Panel>
  );
}

function AccountAccess({
  record,
  isSelf,
  canUpdate,
  canSetPassword,
}: {
  readonly record: UserDetail;
  readonly isSelf: boolean;
  readonly canUpdate: boolean;
  readonly canSetPassword: boolean;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { show } = useToast();
  const locale = currentLocale();

  const [activationPrompt, setActivationPrompt] = useState<'deactivate' | 'reactivate' | null>(
    null,
  );
  const [activationReason, setActivationReason] = useState('');
  const [isSettingPassword, setIsSettingPassword] = useState(false);

  const activation = useMutation({
    mutationFn: () =>
      usersApi.update(record.id, {
        isActive: activationPrompt === 'reactivate',
        ...(activationReason.trim() ? { reason: activationReason.trim() } : {}),
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['users'] });
      await queryClient.invalidateQueries({ queryKey: ['user', record.id] });
      setActivationPrompt(null);
      setActivationReason('');
    },
  });

  return (
    <Panel title={t('users.accessSection')}>
      <dl className="grid gap-stack sm:grid-cols-2">
        <ReadOnlyField label={t('common.status')}>
          <StatusBadge
            tone={record.isActive ? 'success' : 'neutral'}
            label={record.isActive ? t('common.active') : t('common.inactive')}
          />
        </ReadOnlyField>
        <ReadOnlyField label={t('users.role')}>{t(`roles.${record.role}`)}</ReadOnlyField>
        <ReadOnlyField label={t('users.lastSignIn')}>
          {formatDateTime(record.lastLoginAt, { locale }) ?? t('common.never')}
        </ReadOnlyField>
        <ReadOnlyField label={t('users.createdOn')}>
          {formatDateTime(record.createdAt, { locale }) ?? t('common.notAvailable')}
        </ReadOnlyField>
        {record.deactivatedAt ? (
          <ReadOnlyField label={t('users.deactivatedOn')}>
            {formatDateTime(record.deactivatedAt, { locale })}
          </ReadOnlyField>
        ) : null}
      </dl>

      {isSelf ? (
        <p className="mt-stack text-caption text-default-600">{t('users.ownPasswordHint')}</p>
      ) : canSetPassword || canUpdate ? (
        <div className="mt-stack flex flex-wrap gap-inline border-t border-default-200 pt-stack">
          {canSetPassword ? (
            <ActionButton priority="secondary" onPress={() => setIsSettingPassword(true)}>
              {t('users.setPassword')}
            </ActionButton>
          ) : null}
          {canUpdate ? (
            record.isActive ? (
              <ActionButton
                priority="destructive"
                onPress={() => setActivationPrompt('deactivate')}
              >
                {t('users.deactivate')}
              </ActionButton>
            ) : (
              <ActionButton priority="secondary" onPress={() => setActivationPrompt('reactivate')}>
                {t('users.reactivate')}
              </ActionButton>
            )
          ) : null}
        </div>
      ) : null}

      <ConfirmDialog
        isOpen={activationPrompt !== null}
        onClose={() => {
          if (!activation.isPending) {
            setActivationPrompt(null);
            activation.reset();
          }
        }}
        title={
          activationPrompt === 'deactivate'
            ? t('users.deactivateTitle')
            : t('users.reactivateTitle')
        }
        message={
          activationPrompt === 'deactivate'
            ? t('users.deactivateBody')
            : t('users.reactivateBody')
        }
        confirmLabel={
          activationPrompt === 'deactivate' ? t('users.deactivate') : t('users.reactivate')
        }
        confirmPriority={activationPrompt === 'deactivate' ? 'destructive' : 'primary'}
        isPending={activation.isPending}
        onConfirm={() => activation.mutateAsync().then(() => undefined, () => undefined)}
      >
        <div className="mt-stack flex flex-col gap-stack">
          <TextAreaField
            label={t('common.reasonOptional')}
            value={activationReason}
            onChange={setActivationReason}
            rows={2}
          />
          {activation.error ? <ErrorState error={activation.error} /> : null}
        </div>
      </ConfirmDialog>

      {canSetPassword && !isSelf ? (
        <SetPasswordDialog
          record={record}
          isOpen={isSettingPassword}
          onClose={() => setIsSettingPassword(false)}
          onDone={() => {
            setIsSettingPassword(false);
            show({
              tone: 'success',
              message: t('users.setPasswordDone', { name: record.displayName }),
            });
          }}
        />
      ) : null}
    </Panel>
  );
}

/**
 * A manager sets a new password for a colleague (D180).
 *
 * The dialog says what will happen before it happens — the password works at
 * once, every session ends, nothing is emailed — and reports success only once
 * the server has done it.
 */
function SetPasswordDialog({
  record,
  isOpen,
  onClose,
  onDone,
}: {
  readonly record: UserDetail;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onDone: () => void;
}) {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const setNewPassword = useMutation({
    mutationFn: () =>
      usersApi.setPassword(record.id, {
        password,
        ...(reason.trim() ? { reason: reason.trim() } : {}),
      }),
    onSuccess: () => {
      clear();
      onDone();
    },
  });

  // Nothing typed here outlives the dialog.
  const clear = () => {
    setPassword('');
    setConfirmation('');
    setReason('');
    setHasSubmitted(false);
    setNewPassword.reset();
  };

  const close = () => {
    if (setNewPassword.isPending) return;
    clear();
    onClose();
  };

  const submit = () => {
    setHasSubmitted(true);
    if (hasNewPasswordProblem(password, confirmation)) return;
    setNewPassword.mutate();
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={close}
      title={t('users.setPasswordTitle')}
      size="lg"
      footer={
        <ActionBar>
          <ActionButton priority="tertiary" onPress={close} isDisabled={setNewPassword.isPending}>
            {t('common.cancel')}
          </ActionButton>
          <ActionButton
            priority="primary"
            onPress={submit}
            isLoading={setNewPassword.isPending}
          >
            {t('users.setPasswordSubmit')}
          </ActionButton>
        </ActionBar>
      }
    >
      <form
        className="flex flex-col gap-stack"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Alert
          tone="warning"
          title={record.displayName}
          description={t('users.setPasswordBody', { name: record.displayName })}
        />
        <NewPasswordFields
          password={password}
          confirmation={confirmation}
          onPasswordChange={setPassword}
          onConfirmationChange={setConfirmation}
          showProblems={hasSubmitted}
          isDisabled={setNewPassword.isPending}
        />
        <TextAreaField
          label={t('common.reasonOptional')}
          value={reason}
          onChange={setReason}
          rows={2}
        />
        {setNewPassword.error ? <ErrorState error={setNewPassword.error} /> : null}
      </form>
    </Dialog>
  );
}
